import { useEffect, useLayoutEffect, useRef, useCallback, useState } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Button } from '@pantry/ui';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { logger } from '../../lib/logger';

interface ContinuousBarcodeScannerProps {
  /** Called when a barcode is scanned. Should return true if the barcode was handled. */
  onScan: (barcode: string) => boolean | Promise<boolean>;
  /** Called when the scanner is closed */
  onClose: () => void;
  /** Number of items currently in the batch */
  itemCount?: number;
}

interface RecentScan {
  barcode: string;
  timestamp: number;
}

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
];

/** Minimum time between duplicate barcode scans (ms) */
const DUPLICATE_DEBOUNCE_MS = 1500;

/** How long to show recent scans in the overlay (ms) */
const RECENT_SCAN_DISPLAY_MS = 3000;

/**
 * Continuous barcode scanner for batch item entry.
 * Unlike WebBarcodeScanner, this doesn't close after each scan.
 * Shows recent scans overlay and total count.
 */
export function ContinuousBarcodeScanner({
  onScan,
  onClose,
  itemCount = 0,
}: ContinuousBarcodeScannerProps) {
  const { t } = useLingui();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [recentScans, setRecentScans] = useState<RecentScan[]>([]);
  const lastScanRef = useRef<{ barcode: string; timestamp: number } | null>(null);
  const processingRef = useRef(false);

  // Stable references to avoid stale closures
  const onScanRef = useRef(onScan);
  const onCloseRef = useRef(onClose);

  useLayoutEffect(() => {
    onScanRef.current = onScan;
    onCloseRef.current = onClose;
  });

  // Clear old recent scans periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setRecentScans((prev) =>
        prev.filter((scan) => now - scan.timestamp < RECENT_SCAN_DISPLAY_MS)
      );
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Stop camera and release all resources
  const stopCamera = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;

    scannerRef.current = null;

    try {
      const state = scanner.getState();
      if (state === 2 || state === 3) {
        await scanner.stop();
      }
    } catch (err) {
      logger.debug('Error stopping scanner', { error: String(err) });
    }

    try {
      scanner.clear();
    } catch (err) {
      logger.debug('Error clearing scanner', { error: String(err) });
    }
  }, []);

  useEffect(() => {
    const readerId = 'continuous-barcode-reader';
    let isCancelled = false;
    let localScanner: Html5Qrcode | null = null;

    const initScanner = async () => {
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (isCancelled) return;

      const readerElement = document.getElementById(readerId);
      if (readerElement) {
        readerElement.innerHTML = '';
      }

      const scanner = new Html5Qrcode(readerId, {
        formatsToSupport: SUPPORTED_FORMATS,
        verbose: false,
      });
      localScanner = scanner;

      if (isCancelled) {
        try {
          scanner.clear();
        } catch {
          // Ignore
        }
        return;
      }

      scannerRef.current = scanner;

      try {
        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 280, height: 180 },
            aspectRatio: 1.5,
          },
          async (decodedText: string) => {
            if (isCancelled || processingRef.current) return;

            // Check for duplicate scan within debounce window
            const now = Date.now();
            const lastScan = lastScanRef.current;
            if (
              lastScan &&
              lastScan.barcode === decodedText &&
              now - lastScan.timestamp < DUPLICATE_DEBOUNCE_MS
            ) {
              return;
            }

            processingRef.current = true;
            lastScanRef.current = { barcode: decodedText, timestamp: now };

            try {
              // Call the onScan handler
              const handled = await onScanRef.current(decodedText);

              if (handled) {
                // Add to recent scans display
                setRecentScans((prev) => [
                  { barcode: decodedText, timestamp: now },
                  ...prev.slice(0, 4), // Keep last 5 scans
                ]);
              }
            } finally {
              processingRef.current = false;
            }
          },
          () => {
            // Scan error - ignore, keep scanning
          }
        );
      } catch (err) {
        logger.error('Failed to start scanner', err);
        if (!isCancelled) {
          setError(
            err instanceof Error
              ? err.message
              : t`Failed to access camera. Please ensure camera permissions are granted.`
          );
        }
      }
    };

    initScanner();

    return () => {
      isCancelled = true;

      const scanner = localScanner || scannerRef.current;
      scannerRef.current = null;

      if (scanner) {
        (async () => {
          try {
            const state = scanner.getState();
            if (state === 2 || state === 3) {
              await scanner.stop();
            }
          } catch {
            // Ignore
          }
          try {
            scanner.clear();
          } catch {
            // Ignore
          }
        })();
      }
    };
  }, []);

  const handleClose = useCallback(async () => {
    if (isClosing) return;
    setIsClosing(true);

    await stopCamera();
    onCloseRef.current();
  }, [isClosing, stopCamera]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80">
      <div className="relative w-full max-w-md mx-4 bg-card rounded-lg overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold">
              <Trans>Continuous Scan</Trans>
            </h3>
            <p className="text-sm text-muted-foreground">
              <Plural value={itemCount ?? 0} one="# item added" other="# items added" />
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={handleClose}
            disabled={isClosing}
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        {/* Scanner Container */}
        <div className="relative bg-black">
          <div id="continuous-barcode-reader" className="w-full" />

          {/* Recent Scans Overlay */}
          {recentScans.length > 0 && (
            <div className="absolute top-2 left-2 right-2 space-y-1">
              {recentScans.slice(0, 3).map((scan, index) => (
                <div
                  key={`${scan.barcode}-${scan.timestamp}`}
                  className="flex items-center gap-2 bg-green-500/90 text-white px-3 py-1.5 rounded-md text-sm animate-in fade-in slide-in-from-top-2 duration-200"
                  style={{
                    opacity: 1 - index * 0.25,
                    animationDelay: `${index * 50}ms`,
                  }}
                >
                  <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate font-mono">{scan.barcode}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 text-center">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Instructions */}
        {!error && (
          <div className="p-4 text-center text-sm text-muted-foreground">
            <p>
              <Trans>Scan items continuously - they will be added automatically</Trans>
            </p>
            <p className="mt-1">
              <Trans>Tap Done when finished</Trans>
            </p>
          </div>
        )}

        {/* Done Button */}
        <div className="p-4 pt-0">
          <Button variant="default" className="w-full" onClick={handleClose} disabled={isClosing}>
            {isClosing ? (
              <Trans>Closing...</Trans>
            ) : (
              <Trans>
                Done (<Plural value={itemCount ?? 0} one="# item" other="# items" />)
              </Trans>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
